import { type ReactNode, createContext, useContext, useMemo } from "react";
import {
  apiClientFunction,
  createRestClient,
  type IRestClient,
  type Middleware,
} from "./ApiClient";
import { getReportToken } from "../report/ReportParams";
import { useLoadReportContext } from "../report/LoadReportContext";

export const TOKEN_HEADER = "X-Token";

const ReportClientContext = createContext<IRestClient | null>(null);

export function useReportClient() {
  const context = useContext(ReportClientContext);

  if (!context) throw new Error("No ReportClientContext detected!");

  return context;
}

export function withReportToken(token: string): Middleware {
  return (base) => (method, uri, content, headers) =>
    base(method, uri, content, {
      ...headers,
      [TOKEN_HEADER]: token,
    });
}

function createReportClient(token: string): IRestClient {
  const middleware = token ? withReportToken(token) : null;
  const send = middleware ? middleware(apiClientFunction) : apiClientFunction;
  return createRestClient(send);
}

export function ReportClientProvider({ children }: { children: ReactNode }) {
  const { report } = useLoadReportContext();
  const token = getReportToken(report.data);

  const client = useMemo(() => createReportClient(token), [token]);

  return (
    <ReportClientContext.Provider value={client}>
      {children}
    </ReportClientContext.Provider>
  );
}
